/**
 * Which stored navigation row a caller actually gets — asked once (#1680).
 *
 * ── the defect ──────────────────────────────────────────────────────────────
 * Two console screens read the same table and answered differently.
 *
 *   · `NavEditMode` (the in-place bar editor) looked for the deployment default
 *     as `rows.find(persona === null && host === 'console')` and then, failing
 *     that, `rows.find(persona === null && host === null)` — a two-rung
 *     fallback with no version tiebreak and no persona rung at all.
 *   · `NavigationPanel` (the `/personas` Navigation tab) looked for
 *     `(d.persona ?? '') === persona && (d.host ?? '') === host` — an EXACT
 *     match, with the host picker defaulting to `'console'` and offering only
 *     `console` and `portal`.
 *
 * The second one has a consequence the first hides: **a stored row with
 * `host = null` cannot be selected in the admin panel at all**, because no
 * option produces `host === ''`. `core.navigation` treats a null host as a
 * WILDCARD — `(e.host is null or e.host = p_host)` — so such a row is served to
 * every client while the screen that exists to manage it cannot show it, edit
 * it, or clear it. The shipped `nav.default` seed is exactly that row.
 *
 * ── what the door actually does ────────────────────────────────────
 * `core.navigation` is DECLARED in `2026080406` §4 and its ordering is rewritten
 * at runtime by `2026081012` §7 — a `do $$` block that reads
 * `pg_get_functiondef`, string-replaces the `order by`, and `execute`s the
 * result. Grepping `create or replace function core.navigation` finds exactly
 * one hit and it is NOT the live body (rule 1, and the runtime-patch variant of
 * it: three of these four doors are patched this way, `set_navigation` three
 * times, last at `2026082921`). The live text is:─────────────
 *
 *     where e.kind = 'nav' and e.status = 'active'
 *       and (e.persona is null or e.persona = v_persona)
 *       and (e.host    is null or e.host    = p_host)
 *     order by ((e.persona is not null)::int + (e.host is not null)::int) desc,
 *              e.version desc, e.updated_at desc, e.key collate "C"
 *     limit 1
 *
 * Three things a client copy keeps getting wrong, all of them here:
 *
 *   1. **Null is a wildcard, not a value.** A row is a candidate when its
 *      persona/host is null OR equal — not only when equal.
 *   2. **Specificity is a SUM, so two rungs TIE.** `(persona = X, host = null)`
 *      and `(persona = null, host = 'console')` both score 1. The door does not
 *      prefer persona over host; it falls straight through to version, then
 *      `updated_at`, then the key. A client that invents a persona-first ladder
 *      will eventually name a different bar than the one the person has.
 *   3. **The tiebreak is total on purpose.** 2026081012 records why: `mbn` had
 *      `nav.default` and `nav.customer` both at `(null, null, version 1)`, so
 *      `limit 1` took whichever the executor emitted first — stable until a
 *      VACUUM. Dropping the last two ordering terms here would reintroduce
 *      exactly that, in the client instead of the database. And it is not
 *      hypothetical for a client to have to care: `core.experience`
 *      (`core 0021`) has ONE unique constraint, `key`, and none on
 *      `(kind, persona, host)`. One row per address is a CONVENTION held up by
 *      `set_navigation`'s key composition, not a constraint — any other writer
 *      can put two rows at the same address, and then only the tiebreak decides.
 *
 * ── two questions, not one ─────────────────────────────────────────────────
 * `effectiveNavigationRow` answers *"what will a person at (persona, host)
 * actually see"*. `navigationRowAt` answers *"which row am I editing"* — an
 * exact address, because an editor that silently edits an inherited row writes
 * somebody else's layer. The admin panel needs BOTH and was conflating them.
 */

/**
 * The authoring rungs, as the SERVER names them. Not a vocabulary this package
 * invents: `core.experience_rung` maps a nullable `scope` onto exactly these
 * six, and `core.navigation_defaults()` transports the result (#2035).
 *
 * `unreconciled` is what a NULL scope becomes — a row no writer ever attributed.
 * It is never backfilled, relabelled or deleted, and it ranks below every
 * stated rung rather than tying with `system`.
 */
export const NAV_RUNGS = ['user', 'tenant', 'solution', 'persona', 'system', 'unreconciled'] as const
export type NavRung = typeof NAV_RUNGS[number]

/** A row as `core.navigation_defaults()` returns it. Structural on purpose. */
export interface StoredNavigationRow {
  key: string
  persona: string | null
  host: string | null
  version: number
  /** `updated_at` as the door orders by it. Absent is treated as oldest. */
  updated_at?: string | null
  /**
   * The authoring rung, transported by the door since core 0.348.0. Optional
   * because a client may still be talking to an older deployment, and the
   * fallback below is chosen so that such a client degrades to ONE consistent
   * answer rather than to a subtly different ladder.
   */
  rung_rank?: number | null
}

/**
 * The door's rank, transported rather than recomputed (rule 42).
 *
 * ── what this used to be, and why it changed ────────────────────────────────
 * This was `specificity()`: `(persona !== null) + (host !== null)`, a SUM, so
 * `(persona, null)` and `(null, host)` TIED and version decided. That mirrored
 * `core.navigation` exactly, and this file's header still explains why a client
 * must not invent a ladder of its own.
 *
 * #1808 changed the DOOR. It ranks by WHO AUTHORED the layer — user > tenant >
 * solution > persona > system > unreconciled — so the mirror became the thing
 * its own header warned about: "a client that invents a persona-first ladder
 * will eventually name a different bar than the one the person has." It was not
 * repairable here, because `core.navigation_defaults()` returned no `scope`.
 * #2035 transports it, and this reads it.
 *
 * A row with no `rung_rank` gets 0 — BELOW every stated rung, including
 * `unreconciled`'s 10. An old deployment therefore ranks every row equally and
 * falls through to the version/updated_at/key tiebreak, which is exactly the
 * pre-#1808 behaviour minus the targeting sum. It never invents a rung.
 */
function rung(row: StoredNavigationRow): number {
  return row.rung_rank ?? 0
}

/**
 * Persona-exactness, then host-exactness — SEPARATE terms, in that order.
 *
 * The sum is gone on purpose. Within one rung the door breaks the tie by
 * persona first and host second (#1808 ruling §2, confirmed 66267 §3); summing
 * them made those two indistinguishable, which is how `(persona, null)` and
 * `(null, host)` came to tie.
 */
function personaExact(row: StoredNavigationRow): number { return row.persona !== null ? 1 : 0 }
function hostExact(row: StoredNavigationRow): number { return row.host !== null ? 1 : 0 }

/** Byte-order comparators, so the two tiebreaks read as what they are. */
const asc = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)
const desc = (a: string, b: string): number => asc(b, a)

/** The door's candidate predicate: null is a wildcard, not a value. */
function candidate(row: StoredNavigationRow, persona: string | null, host: string | null): boolean {
  return (row.persona === null || row.persona === persona)
    && (row.host === null || row.host === host)
}

/**
 * The row `core.navigation(persona, host)` would resolve, or `undefined`.
 *
 * The caller's own pin is NOT considered — it is a different table and a
 * different door (`core.pin_navigation`), and it always wins. This answers the
 * rung below it, which is the question both screens were asking.
 */
export function effectiveNavigationRow<T extends StoredNavigationRow>(
  rows: readonly T[], persona: string | null, host: string | null,
): T | undefined {
  return rows
    /* `.filter` already returns a new array, so the sort below cannot reach the
       caller's. There WAS a `.slice()` here and mutation row 7 proved it inert:
       removing it changed nothing. The property still has a test, because the
       thing that would break it is somebody rewriting this as `rows.sort(...)`
       — and reordering the admin panel's list as a side effect of reading it is
       the kind of bug that only shows up on the second render. */
    .filter((row) => candidate(row, persona, host))
    .sort((a, b) =>
      // The door's ORDER BY, term for term. Any divergence here is the #1680
      // defect coming back, so the parity witness in packages/core drives BOTH
      // this comparator and `core.navigation` over the same rows.
      rung(b) - rung(a)
      || personaExact(b) - personaExact(a)
      || hostExact(b) - hostExact(a)
      || b.version - a.version
      // Byte order both times, not `localeCompare`: the door says
      // `collate "C"`, and a locale collation would disagree with it on any
      // key a module is free to choose. `<` on a JS string is code-unit order,
      // which is the same answer for the ASCII these keys are.
      || desc(a.updated_at ?? '', b.updated_at ?? '')
      || asc(a.key, b.key))[0]
}

/**
 * The row stored at EXACTLY this address, or `undefined`.
 *
 * What an editor is editing. `null` means null here — the deployment-wide,
 * host-agnostic row is `(null, null)` and is addressable, which is the thing
 * the admin panel could not say.
 */
export function navigationRowAt<T extends StoredNavigationRow>(
  rows: readonly T[], persona: string | null, host: string | null,
): T | undefined {
  return rows.find((row) => row.persona === persona && row.host === host)
}
